import React, { FC, MutableRefObject, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { IsStringEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { URLConstructor } from '../../../../NonReact/utils/URLBuilder'
import { LanguageContext, ReactState } from '../../Contexts'
import { OAUTH_RESPONSE_TYPE, OAUTH_WINDOW_SIZING } from './Constants/Constants'
import { OAuthConfig } from './Types/Types'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { OAuthButton as OAuthButtonLanguage } from '../../../../../i18n'
import Button from '../Button'
import Loader from '../Loader'
import Icon, { IconName } from '../Icon'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { usePropAsRef } from '../../Hooks/usePropAsRef'
import { getOAuthIntegrationStatus } from '../../../../NonReact/constants/ApiConstants'
import { TelSysTypes } from '../../../../NonReact/constants/TelephonyServerTypes'
import { OAuthStatusResponse } from '../../../../NonReact/Types/Types'
import { StateSetter } from '../../Types/Types'
import { ALL_VALUE } from '../../API/ItemsGetters'
import { useFormContext, UseFormMethods } from '../../../../Vendor/react-hook-form/dist/index.ie11'
import { OAUTH_SESSION_ID_FIELD_NAME } from '../../../TelSysConfiguration/Constants/FieldNames'
import { executeOAuthFlow } from './Functions/OAuthFlow'
import { RoundedButton } from '../../../../ReactRouting/Components/Atoms/RoundedButton/RoundedButton'
import { JSONResponse } from '../../../../NonReact/common/Types/Types'
import { configureFullURLForLocalDevelopment } from '../../Utils/LocalUtils'

export type OAuthMessagingOptions = {
    authorisationButtonTitle?: string
    authorisedMessage?: string
    preAuthorisationButtonTitle?: string
    preAuthorisedMessage?: string
}

type Props = {
    config?: Nullable<OAuthConfig>
    overrideClientID?: string
    isAlreadyAuthenticated?: boolean
    integrationType?: string
    selectedTelSys?: string | number
    setSessionID?: StateSetter<Nullable<number>>
    onAuthenticating?: () => void
    onOAuthDisplayStatus?: (statuses: ReactElement[]) => void
    onOAuthSuccess?: (sessionID?: number) => void
    onOAuthFailure?: (error?: Error) => void
    lastError?: Nullable<string>
    oAuthMessageOptions?: Nullable<OAuthMessagingOptions>
    isRouterMode?: boolean
    shrink?: boolean
    forceRelogin?: boolean
}

export const OAuthButton: FC<Props> = ({
    config,
    overrideClientID,
    isAlreadyAuthenticated,
    integrationType,
    selectedTelSys,
    setSessionID,
    onAuthenticating,
    onOAuthDisplayStatus,
    onOAuthSuccess,
    onOAuthFailure,
    lastError,
    oAuthMessageOptions,
    isRouterMode,
    shrink,
    forceRelogin
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(OAuthButtonLanguage.NAME)

    let methods: UseFormMethods

    if (!setSessionID) {
        methods = useFormContext()
    }

    const [isAuthorising, setIsAuthorising]: ReactState<boolean> = useState<boolean>(false)
    const [isPreAuthorised, setIsPreAuthorised]: ReactState<Nullable<boolean>> = useState<Nullable<boolean>>()
    const [isAuthorised, setIsAuthorised]: ReactState<boolean> = useState<boolean>(false)
    const [PreAuthorisationError, setPreAuthorisationError]: ReactState<Nullable<string>> = useState<Nullable<string>>()
    const [authorisationError, setAuthorisationError]: ReactState<Nullable<string>> = useState<Nullable<string>>()
    const [sessionIDOAuthButton, setSessionIDOAuthButton]: ReactState<number> = useState<number>(0)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const overrideClientIDAsRef: MutableRefObject<string> = usePropAsRef<string>(overrideClientID ?? EMPTY_STRING)

    const calculatedButtonTitle: string = useMemo((): string => {
        if (isStringNotEmpty(config?.PreAuthorisationURL) && !isPreAuthorised) {
            return (
                oAuthMessageOptions?.preAuthorisationButtonTitle ??
                language.get(OAuthButtonLanguage.PreAuthoriseButton_Title)
            )
        }

        return oAuthMessageOptions?.authorisationButtonTitle ?? language.get(OAuthButtonLanguage.AuthoriseButton_Title)
    }, [oAuthMessageOptions, isPreAuthorised])

    const doAuthFlow = useCallback(
        (
            { AuthenticationEndpoint, ClientID, RedirectURL, Scope, ExternalUserID, RequiresConsent }: OAuthConfig,
            sessionIDLocal: number,
            loggedInUrl: string
        ): void => {
            setIsAuthorised(false)
            setAuthorisationError(undefined)
            const sessionIdPart: string = `${ExternalUserID}.${sessionIDLocal}.${
                isStringNotEmpty(selectedTelSys?.toString()) ? selectedTelSys : ALL_VALUE
            }`

            executeOAuthFlow<OAuthStatusResponse>(
                URLConstructor.newInstance({
                    baseURL: AuthenticationEndpoint,
                    queryParams: {
                        client_id: isStringNotEmpty(overrideClientIDAsRef.current)
                            ? overrideClientIDAsRef.current
                            : ClientID,
                        redirect_uri: configureFullURLForLocalDevelopment(RedirectURL),
                        response_type: OAUTH_RESPONSE_TYPE,
                        state: `${loggedInUrl}|${sessionIdPart}`,
                        scope: Scope,
                        ...(RequiresConsent ? { prompt: 'consent' } : {}),
                        ...(isVarNotEmpty(forceRelogin) ? { force_auth: 'true', max_age: '0', prompt: 'login' } : {})
                    }
                }),
                `OAuthAuthoriseWindow_${sessionIDLocal}`,
                OAUTH_WINDOW_SIZING,
                getOAuthIntegrationStatus(integrationType ?? TelSysTypes.UNKNOWN, sessionIDLocal),
                (status: JSONResponse<OAuthStatusResponse>): boolean => !!status?.HasRefreshToken,
                language.get(OAuthButtonLanguage.Authentication_Failed)
            )
                .then((): void => {
                    if (isMounted.current) {
                        setIsAuthorising(false)
                        setIsAuthorised(true)
                        onOAuthSuccess?.(sessionIDLocal ?? 0)
                    }
                })
                .catch((err: Error): void => {
                    if (isMounted.current) {
                        setAuthorisationError(err?.message)
                        setPreAuthorisationError(undefined)
                        setIsAuthorising(false)
                        setIsAuthorised(false)
                        setIsPreAuthorised(false)
                        onOAuthFailure?.(err)
                    }
                })
        },
        [overrideClientID, integrationType]
    )

    const doConsentFlow = useCallback(
        (oAuthConfig: OAuthConfig, sessionIDLocal: number, loggedInUrl: string): void => {
            const { PreAuthorisationURL, PreAuthorisationScope, ClientID, RedirectURL, ExternalUserID }: OAuthConfig =
                oAuthConfig

            setAuthorisationError(undefined)
            setPreAuthorisationError(undefined)
            setIsPreAuthorised(false)

            executeOAuthFlow<OAuthStatusResponse>(
                URLConstructor.newInstance({
                    baseURL: PreAuthorisationURL,
                    queryParams: {
                        client_id: isStringNotEmpty(overrideClientIDAsRef.current)
                            ? overrideClientIDAsRef.current
                            : ClientID,
                        redirect_uri: configureFullURLForLocalDevelopment(RedirectURL),
                        scope: PreAuthorisationScope,
                        // Carry the ExternalUserID in the state (like the authorise flow) so the CCS
                        // admin-consent redirect can resolve the user session-independently. The consent
                        // popup and the New-UI iframe are separate CCS sessions cross-site, so a
                        // JSESSIONID-based lookup stores the grant on the wrong user and /status never
                        // sees it (APD-13442).
                        state: `${loggedInUrl}|${ExternalUserID}.${sessionIDLocal}`
                    }
                }),
                `OAuthAuthoriseWindow_${sessionIDLocal}`,
                OAUTH_WINDOW_SIZING,
                getOAuthIntegrationStatus(integrationType ?? TelSysTypes.UNKNOWN, sessionIDLocal),
                (status: JSONResponse<OAuthStatusResponse>): boolean => !!status?.PreAuthorised,
                language.get(OAuthButtonLanguage.Authentication_Failed)
            )
                .then((): void => {
                    if (isMounted.current) {
                        setIsPreAuthorised(true)
                    }
                })
                .catch((err: Error): void => {
                    if (isMounted.current) {
                        setPreAuthorisationError(err.message)
                        setIsPreAuthorised(false)
                        setIsAuthorising(false)
                        setIsAuthorised(false)
                        onOAuthFailure?.(err)
                    }
                })
        },
        [integrationType]
    )

    const onClick = useCallback((): void => {
        if (isVarNotEmpty(config)) {
            const { PreAuthorisationURL, PreAuthorisationScope }: OAuthConfig = config
            let sessionIDLocal: number = sessionIDOAuthButton

            const loggedInUrl = window.location.origin
            if (!isPreAuthorised) {
                sessionIDLocal = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)

                setSessionIDOAuthButton(sessionIDLocal)
            }

            if (setSessionID) {
                setSessionID(sessionIDLocal)
            } else {
                methods?.setValue(OAUTH_SESSION_ID_FIELD_NAME, sessionIDLocal)
            }

            onAuthenticating?.()
            setIsAuthorising(true)

            if (isStringNotEmpty(PreAuthorisationURL) && isStringNotEmpty(PreAuthorisationScope) && !isPreAuthorised) {
                doConsentFlow(config, sessionIDLocal, loggedInUrl)
            } else {
                doAuthFlow(config, sessionIDLocal, loggedInUrl)
            }
        }
    }, [config, isPreAuthorised, sessionIDOAuthButton])

    const createStatusElement = (icon: IconName, text: string, warning?: Nullable<string>): ReactElement => (
        <>
            <div
                className={`ocr-status-container ${
                    icon === IconName.SUCCESS ? 'ocr-status-success' : 'ocr-status-error'
                }`}
            >
                <Icon className={`${icon === IconName.SUCCESS ? 'success' : 'error'}-oauth-icon`} iconName={icon} />
                <span className='status-text'>{text}</span>
            </div>
            {isStringNotEmpty(warning) ? (
                <div className='ocr-warning'>
                    <Icon className='warning-oauth-icon' iconName={IconName.WARNING} />
                    <span className='status-text'>{lastError}</span>
                </div>
            ) : (
                <></>
            )}
        </>
    )

    useEffect(() => {
        if (isMounted.current) {
            setAuthorisationError(undefined)
            setIsPreAuthorised(undefined)
            setIsAuthorised(false)
            setPreAuthorisationError(undefined)
            setIsAuthorising(false)
        }
    }, [JSON.stringify(config)])

    useEffect((): void => {
        const statuses: ReactElement[] = []

        if (isVarNotEmpty(PreAuthorisationError)) {
            statuses.push(createStatusElement(IconName.ERRORCIRCLE, PreAuthorisationError))
        }

        if (isVarNotEmpty(authorisationError)) {
            statuses.push(createStatusElement(IconName.ERRORCIRCLE, authorisationError))
        }

        if (
            isAlreadyAuthenticated &&
            !isAuthorising &&
            !isAuthorised &&
            !isPreAuthorised &&
            IsStringEmpty(authorisationError) &&
            IsStringEmpty(PreAuthorisationError)
        ) {
            statuses.push(
                createStatusElement(
                    IconName.SUCCESS,
                    language.get(OAuthButtonLanguage.Authentication_Authenticated),
                    lastError
                )
            )
        }

        if (isPreAuthorised) {
            statuses.push(
                createStatusElement(
                    IconName.SUCCESS,
                    oAuthMessageOptions?.preAuthorisedMessage ??
                        language.get(OAuthButtonLanguage.Authentication_PreAuthorisation_Successful)
                )
            )
        }

        if (isAuthorised) {
            statuses.push(
                createStatusElement(
                    IconName.SUCCESS,
                    oAuthMessageOptions?.authorisedMessage ??
                        language.get(OAuthButtonLanguage.Authentication_Successful)
                )
            )
        }

        onOAuthDisplayStatus?.(statuses)
    }, [
        PreAuthorisationError,
        authorisationError,
        isAlreadyAuthenticated,
        isAuthorising,
        isAuthorised,
        isPreAuthorised
    ])

    if (isVarNotEmpty(config)) {
        return (
            <div className='oauth-container'>
                <div className='oc-button'>
                    {isRouterMode ? (
                        <RoundedButton type='button' onClick={onClick} shrink={shrink}>
                            {calculatedButtonTitle}
                        </RoundedButton>
                    ) : (
                        <Button type='button' onClick={onClick}>
                            {calculatedButtonTitle}
                        </Button>
                    )}
                </div>
                {isPreAuthorised ||
                isAuthorised ||
                isAuthorising ||
                isAlreadyAuthenticated ||
                isStringNotEmpty(authorisationError) ||
                isStringNotEmpty(PreAuthorisationError) ? (
                    <div className='oc-status'>
                        <div className='oc-status-title'>
                            {language.get(OAuthButtonLanguage.Authentication_Status_Title)}
                        </div>

                        {onOAuthDisplayStatus ? (
                            <></>
                        ) : (
                            <div className='oc-info-container'>
                                {isVarNotEmpty(PreAuthorisationError) ? (
                                    createStatusElement(IconName.ERRORCIRCLE, PreAuthorisationError)
                                ) : (
                                    <></>
                                )}

                                {isVarNotEmpty(authorisationError) ? (
                                    createStatusElement(IconName.ERRORCIRCLE, authorisationError)
                                ) : (
                                    <></>
                                )}

                                {isAlreadyAuthenticated &&
                                !isAuthorising &&
                                !isAuthorised &&
                                !isPreAuthorised &&
                                IsStringEmpty(authorisationError) &&
                                IsStringEmpty(PreAuthorisationError) ? (
                                    createStatusElement(
                                        IconName.SUCCESS,
                                        language.get(OAuthButtonLanguage.Authentication_Authenticated),
                                        lastError
                                    )
                                ) : (
                                    <></>
                                )}

                                {isPreAuthorised ? (
                                    createStatusElement(
                                        IconName.SUCCESS,
                                        oAuthMessageOptions?.preAuthorisedMessage ??
                                            language.get(OAuthButtonLanguage.Authentication_PreAuthorisation_Successful)
                                    )
                                ) : (
                                    <></>
                                )}

                                {isAuthorised ? (
                                    createStatusElement(
                                        IconName.SUCCESS,
                                        oAuthMessageOptions?.authorisedMessage ??
                                            language.get(OAuthButtonLanguage.Authentication_Successful)
                                    )
                                ) : (
                                    <></>
                                )}

                                {isAuthorising ? <Loader /> : <></>}
                            </div>
                        )}
                    </div>
                ) : (
                    <></>
                )}
            </div>
        )
    }

    return <></>
}
